import { STALE_TIME_FIVE_MINUTES_MS } from '@/shared/constants/query-stale-time.constants';
import {
  productSettingsService,
  type ProductEmissionMode,
} from '@/services/product-settings';
import { useQuery } from '@tanstack/react-query';

export const PRODUCT_SETTINGS_QUERY_KEY = ['product-settings'] as const;

/**
 * Modo de emissão dos produtos, indexado por `productCode`.
 *
 * O BFF devolve SÓ os produtos com configuração gravada; produto ausente é
 * `INTEGRATION` (o padrão do domínio), então o mapa é consultado com esse
 * fallback em vez de exigir uma entrada por produto.
 */
export function useProductSettings() {
  return useQuery({
    queryKey: PRODUCT_SETTINGS_QUERY_KEY,
    queryFn: async () => {
      const response = await productSettingsService.listProductSettings();
      if (!response.success || !response.data) {
        throw new Error(
          response.error || 'Erro ao carregar as configurações de produto'
        );
      }
      return response.data;
    },
    staleTime: STALE_TIME_FIVE_MINUTES_MS,
  });
}

/**
 * Modo de emissão de UM produto.
 *
 * Enquanto carrega devolve `undefined` — quem gateia um botão por "modo manual"
 * deve tratar `undefined` como "ainda não sei" e não renderizar, para o botão
 * não piscar num produto de emissão automática.
 */
export function useProductEmissionMode(productCode?: string | null) {
  const { data, isLoading, error } = useProductSettings();

  const emissionMode: ProductEmissionMode | undefined = (() => {
    if (isLoading || !data) return undefined;
    if (!productCode) return 'INTEGRATION';
    return (
      data.find(settings => settings.productCode === productCode)
        ?.emissionMode ?? 'INTEGRATION'
    );
  })();

  return {
    emissionMode,
    isManualEmission: emissionMode === 'MANUAL',
    isLoading,
    error,
  };
}
